import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { MenuSet, AddOn } from '@/lib/index';

export interface CartItem {
  id: string;
  name: string;
  price: number;
  quantity: number;
  type: 'menu' | 'addon';
  imageKey?: string;
}

interface CartStore {
  items: CartItem[];
  isOpen: boolean;
  addItem: (item: MenuSet | AddOn, type: 'menu' | 'addon') => void;
  removeItem: (id: string) => void;
  updateQuantity: (id: string, quantity: number) => void;
  clearCart: () => void;
  getTotalPrice: () => number;
  getTotalItems: () => number;
  openCart: () => void;
  closeCart: () => void;
  toggleCart: () => void;
}

export const useCart = create<CartStore>()(
  persist(
    (set, get) => ({
      items: [] as CartItem[],
      isOpen: false,
      
      addItem: (item, type) => {
        const existingItem = get().items.find(cartItem => cartItem.id === item.id);
        
        if (existingItem) {
          set(state => ({
            items: state.items.map(cartItem =>
              cartItem.id === item.id
                ? { ...cartItem, quantity: cartItem.quantity + 1 }
                : cartItem
            )
          }));
        } else {
          const newItem: CartItem = {
            id: item.id,
            name: item.name,
            price: item.price,
            quantity: 1,
            type,
            imageKey: type === 'menu' ? (item as MenuSet).imageKey : undefined
          };
          
          set(state => ({
            items: [...state.items, newItem]
          }));
        }
      },
      
      removeItem: (id) => {
        set(state => ({
          items: state.items.filter(item => item.id !== id)
        }));
      },
      
      updateQuantity: (id, quantity) => {
        if (quantity <= 0) {
          get().removeItem(id);
          return;
        }
        
        set(state => ({
          items: state.items.map(item =>
            item.id === id ? { ...item, quantity } : item
          )
        }));
      },
      
      clearCart: () => {
        set({ items: [] });
      },
      
      getTotalPrice: () => {
        return get().items.reduce((total, item) => total + (item.price * item.quantity), 0);
      },
      
      getTotalItems: () => {
        return get().items.reduce((total, item) => total + item.quantity, 0);
      },
      
      openCart: () => set({ isOpen: true }),
      closeCart: () => set({ isOpen: false }),
      toggleCart: () => set(state => ({ isOpen: !state.isOpen }))
    }),
    {
      name: 'nasi-arab-cart'
    }
  )
);